import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TruncatedText } from "@/components/ui/truncated-text";
import { VirtualizedCombobox } from "./VirtualizedCombobox";

interface KeywordSwapSelectorProps {
  keywordOptions: string[];
  keywordToRemove: string;
  targetPhrase: string;
  onKeywordToRemoveChange: (value: string) => void;
  onTargetPhraseChange: (value: string) => void;
}

export function KeywordSwapSelector({
  keywordOptions,
  keywordToRemove,
  targetPhrase,
  onKeywordToRemoveChange,
  onTargetPhraseChange,
}: KeywordSwapSelectorProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const removeOptions = keywordOptions.filter((kw) => kw !== targetPhrase);
  const addOptions = keywordOptions.filter((kw) => kw !== keywordToRemove);

  return (
    <div className="space-y-2" ref={setContainer}>
      <Label>Keyword swap</Label>
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Remove</p>
          <Popover open={removeOpen} onOpenChange={setRemoveOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={removeOpen}
                className="w-full justify-between bg-card font-normal h-9 px-3 hover:bg-primary/15"
              >
                <TruncatedText
                  className={cn("text-sm", keywordToRemove ? "text-foreground" : "text-muted-foreground")}
                >
                  {keywordToRemove || "Select keyword..."}
                </TruncatedText>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              container={container}
              className="w-full p-0"
              align="start"
              style={{ width: "var(--radix-popover-trigger-width)" }}
            >
              <VirtualizedCombobox
                options={removeOptions}
                selected={keywordToRemove}
                searchPlaceholder="Search current keywords..."
                emptyMessage="No keyword found."
                onSelect={(kw) => {
                  onKeywordToRemoveChange(kw);
                  setRemoveOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="pb-2 text-xs font-medium text-muted-foreground">&rarr;</div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add</p>
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={addOpen}
                className="w-full justify-between bg-card font-normal h-9 px-3 hover:bg-primary/15"
              >
                <TruncatedText
                  className={cn("text-sm", targetPhrase ? "text-foreground" : "text-muted-foreground")}
                >
                  {targetPhrase || "Select keyword..."}
                </TruncatedText>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              container={container}
              className="w-full p-0"
              align="start"
              style={{ width: "var(--radix-popover-trigger-width)" }}
            >
              <VirtualizedCombobox
                options={addOptions}
                selected={targetPhrase}
                searchPlaceholder="Search keywords..."
                emptyMessage="No keyword found. Press Enter to use this phrase."
                onSelect={(kw) => {
                  onTargetPhraseChange(kw);
                  setAddOpen(false);
                }}
                searchValue={targetPhrase}
                onSearchChange={onTargetPhraseChange}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}
